import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";

export default function HeadBranchApproval() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [stateFilter, setStateFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(null);

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      const date = value?.toDate ? value.toDate() : new Date(value);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const loadBranches = async () => {
    try {
      setLoading(true);

      const q = query(
        collection(db, "nafed_users"),
        where("role", "==", "STATE_BRANCH")
      );

      const snap = await getDocs(q);

      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );

      setBranches(data);
    } catch (e) {
      console.error("Branch loading failed:", e);
      alert("Branch loading failed. Please check Firestore rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  const getApprovalStatus = (branch) => {
    if (branch.status === "APPROVED") return "APPROVED";
    if (branch.status === "REJECTED") return "REJECTED";
    if (branch.status === "PENDING") return "PENDING";
    if (branch.status === "PENDING_ROLE_APPROVAL") return "PENDING";
    if (branch.headApprovalStatus) return branch.headApprovalStatus;
    return "PENDING";
  };

  const getStatusLabel = (status) => {
    if (status === "APPROVED") return "Approved";
    if (status === "REJECTED") return "Rejected / Returned";
    return "Pending Approval";
  };

  const updateStatus = async (branch, actionStatus) => {
    const remark =
      actionStatus === "REJECTED"
        ? window.prompt("Enter rejection reason:", "Documents incomplete")
        : "Approved by NAFED Head Office";

    if (actionStatus === "REJECTED" && !remark) return;

    const confirmText =
      actionStatus === "APPROVED"
        ? "Are you sure you want to approve this State Branch?"
        : "Are you sure you want to reject / return this State Branch?";

    if (!window.confirm(confirmText)) return;

    try {
      const finalStatus =
        actionStatus === "APPROVED" ? "APPROVED" : "REJECTED";

      const currentUser = auth.currentUser;

      await updateDoc(doc(db, "nafed_users", branch.id), {
        status: finalStatus,
        headApprovalStatus: actionStatus,
        roleApprovalStatus: actionStatus,
        headApprovalRemark: remark,
        roleApprovalRemark: remark,
        headApprovalAt: serverTimestamp(),
        roleApprovedAt: serverTimestamp(),
        approvedByUid: currentUser?.uid || "",
        approvedByEmail: currentUser?.email || "",
        approvedByName: "NAFED Head Office",
        isActive: actionStatus === "APPROVED",
        approvedByRole: "NAFED_HEAD_OFFICE",
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "nafed_mail_notifications"), {
        type: "STATE_BRANCH_APPROVAL_STATUS",
        to: branch.email || "",
        subject:
          actionStatus === "APPROVED"
            ? "NAFED State Branch Registration Approved"
            : "NAFED State Branch Registration Returned",
        message:
          actionStatus === "APPROVED"
            ? `Dear ${
                branch.name || branch.organizationName || "State Branch"
              }, your NAFED State Branch registration has been approved by Head Office.`
            : `Dear ${
                branch.name || branch.organizationName || "State Branch"
              }, your NAFED State Branch registration has been returned. Reason: ${remark}`,
        status: "PENDING",
        relatedCollection: "nafed_users",
        relatedId: branch.id,
        createdAt: serverTimestamp(),
      });

      alert(`Branch ${actionStatus.toLowerCase()} successfully.`);
      loadBranches();
    } catch (e) {
      console.error("Status update failed:", e);
      alert("Status update failed.");
    }
  };

  const stateOptions = useMemo(() => {
    const states = branches.map((b) => b.state).filter(Boolean);
    return ["ALL", ...Array.from(new Set(states))];
  }, [branches]);

  const filteredBranches = useMemo(() => {
    return branches.filter((b) => {
      const status = getApprovalStatus(b);

      const text = `${b.organizationName || ""} ${b.name || ""} ${
        b.email || ""
      } ${b.mobile || ""} ${b.state || ""} ${b.district || ""}`.toLowerCase();

      const matchesSearch = text.includes(searchText.toLowerCase());
      const matchesStatus = filter === "ALL" || status === filter;
      const matchesState = stateFilter === "ALL" || b.state === stateFilter;

      return matchesSearch && matchesStatus && matchesState;
    });
  }, [branches, filter, stateFilter, searchText]);

  const counts = {
    total: branches.length,
    pending: branches.filter((b) => getApprovalStatus(b) === "PENDING").length,
    approved: branches.filter((b) => getApprovalStatus(b) === "APPROVED")
      .length,
    rejected: branches.filter((b) => getApprovalStatus(b) === "REJECTED")
      .length,
  };

  return (
    <>
      <style>{`
        .branch-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .branch-hero {
          background: linear-gradient(135deg, #ffffff, #f8fbff);
          border-radius: 18px;
          padding: 24px;
          box-shadow: 0 12px 30px rgba(15,23,42,.07);
          border: 1px solid #dbe4ef;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .branch-hero h1 {
          margin: 0;
          font-size: 26px;
          font-weight: 950;
          color: #071f45;
        }

        .branch-hero p {
          margin: 7px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .refresh-btn {
          border: 0;
          background: #062b55;
          color: white;
          border-radius: 12px;
          padding: 12px 18px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 10px 18px rgba(6,43,85,.18);
        }

        .branch-kpis {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
        }

        .branch-kpi {
          background: white;
          border-radius: 16px;
          padding: 18px 20px;
          box-shadow: 0 10px 26px rgba(15,23,42,.06);
          border: 1px solid #dbe4ef;
          position: relative;
          overflow: hidden;
        }

        .branch-kpi:before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          height: 4px;
          width: 100%;
          background: #062b55;
        }

        .branch-kpi.pending-card:before { background: #f59e0b; }
        .branch-kpi.approved-card:before { background: #16a34a; }
        .branch-kpi.rejected-card:before { background: #ef4444; }

        .branch-kpi label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .12em;
          color: #64748b;
          font-weight: 950;
        }

        .branch-kpi b {
          display: block;
          margin-top: 8px;
          font-size: 30px;
          font-weight: 950;
          color: #071f45;
        }

        .toolbar {
          background: white;
          border-radius: 16px;
          padding: 14px;
          box-shadow: 0 10px 26px rgba(15,23,42,.06);
          border: 1px solid #dbe4ef;
          display: grid;
          grid-template-columns: 1.4fr .7fr .7fr auto;
          gap: 12px;
          align-items: center;
        }

        .search-input,
        .select-input {
          height: 44px;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 0 12px;
          outline: none;
          font-weight: 800;
          color: #071f45;
          background: #f8fafc;
        }

        .clear-btn {
          height: 44px;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 0 14px;
          background: white;
          color: #071f45;
          font-weight: 950;
          cursor: pointer;
        }

        .branch-panel {
          background: white;
          border-radius: 18px;
          padding: 0;
          box-shadow: 0 12px 30px rgba(15,23,42,.07);
          border: 1px solid #cbd5e1;
          overflow: hidden;
        }

        .panel-head {
          padding: 18px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #cbd5e1;
          background: #ffffff;
        }

        .panel-head h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 950;
          color: #071f45;
        }

        .panel-head span {
          color: #64748b;
          font-size: 12px;
          font-weight: 900;
        }

        .table-wrap {
          overflow-x: auto;
          border-top: 1px solid #cbd5e1;
        }

        .branch-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          min-width: 1380px;
          border: 1px solid #cbd5e1;
        }

        .branch-table th {
          text-align: left;
          background: #eef4fb;
          color: #071f45;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .08em;
          padding: 14px 16px;
          border-bottom: 2px solid #cbd5e1;
          border-right: 1px solid #dbe4ef;
          font-weight: 950;
          white-space: nowrap;
        }

        .branch-table td {
          padding: 16px;
          border-bottom: 1.5px solid #cbd5e1;
          border-right: 1px solid #e2e8f0;
          font-weight: 750;
          vertical-align: top;
          color: #0f172a;
          background: white;
        }

        .branch-table th:last-child,
        .branch-table td:last-child {
          border-right: 0;
        }

        .branch-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .branch-table tr:hover td {
          background: #eef6ff;
        }
                  .org-name {
          font-weight: 950;
          color: #071f45;
          font-size: 13px;
        }

        .muted {
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
          font-weight: 750;
        }

        .status {
          border-radius: 999px;
          padding: 7px 12px;
          font-size: 11px;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }

        .pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .approved {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .rejected {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .action-cell {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
        }

        .action-btn {
          border: 0;
          border-radius: 10px;
          padding: 9px 12px;
          font-weight: 950;
          cursor: pointer;
          font-size: 12px;
        }

        .approve {
          background: #16a34a;
          color: white;
        }

        .reject {
          background: #ef4444;
          color: white;
        }

        .view {
          background: #e8f0ff;
          color: #062b55;
          border: 1px solid #bfd3ff;
        }

        .disabled-btn {
          opacity: .45;
          cursor: not-allowed;
        }

        .empty {
          text-align: center;
          padding: 42px;
          color: #64748b;
          font-weight: 850;
          background: white;
        }

        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(2,6,23,.55);
          display: grid;
          place-items: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-card {
          width: min(780px, 100%);
          background: white;
          border-radius: 20px;
          box-shadow: 0 30px 90px rgba(0,0,0,.25);
          overflow: hidden;
        }

        .modal-head {
          padding: 18px 22px;
          background: #062b55;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-head h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 950;
        }

        .close-btn {
          border: 0;
          background: rgba(255,255,255,.15);
          color: white;
          border-radius: 10px;
          padding: 8px 11px;
          cursor: pointer;
          font-weight: 950;
        }

        .modal-body {
          padding: 22px;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
        }

        .info-box {
          border: 1px solid #cbd5e1;
          background: #f8fafc;
          border-radius: 14px;
          padding: 14px;
        }

        .info-box label {
          display: block;
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          font-weight: 950;
          letter-spacing: .08em;
          margin-bottom: 6px;
        }

        .info-box b {
          color: #071f45;
          font-size: 13px;
          word-break: break-word;
        }

        .modal-actions {
          padding: 16px 22px 22px;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          border-top: 1px solid #cbd5e1;
        }

        @media (max-width: 1000px) {
          .branch-kpis {
            grid-template-columns: repeat(2, 1fr);
          }

          .toolbar {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 560px) {
          .branch-kpis {
            grid-template-columns: 1fr;
          }

          .branch-hero {
            flex-direction: column;
            align-items: flex-start;
          }

          .modal-body {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="branch-page">
        <section className="branch-hero">
          <div>
            <h1>State Branch Approval</h1>
            <p>
              Review, verify and approve State Branch registration requests with
              complete audit details.
            </p>
          </div>

          <button className="refresh-btn" onClick={loadBranches}>
            Refresh
          </button>
        </section>

        <section className="branch-kpis">
          <div className="branch-kpi">
            <label>Total Requests</label>
            <b>{counts.total}</b>
          </div>

          <div className="branch-kpi pending-card">
            <label>Pending Approval</label>
            <b>{counts.pending}</b>
          </div>

          <div className="branch-kpi approved-card">
            <label>Approved Branches</label>
            <b>{counts.approved}</b>
          </div>

          <div className="branch-kpi rejected-card">
            <label>Rejected / Returned</label>
            <b>{counts.rejected}</b>
          </div>
        </section>

        <section className="toolbar">
          <input
            className="search-input"
            placeholder="Search by branch, person, email, mobile, state, district..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <select
            className="select-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>

          <select
            className="select-input"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            {stateOptions.map((s) => (
              <option key={s} value={s}>
                {s === "ALL" ? "All States" : s}
              </option>
            ))}
          </select>

          <button
            className="clear-btn"
            onClick={() => {
              setSearchText("");
              setFilter("ALL");
              setStateFilter("ALL");
            }}
          >
            Clear
          </button>
        </section>

        <section className="branch-panel">
          <div className="panel-head">
            <h3>Branch Registration Requests</h3>
            <span>
              Showing {filteredBranches.length} of {branches.length}
            </span>
          </div>

          {loading ? (
            <div className="empty">Loading branch registrations...</div>
          ) : (
            <div className="table-wrap">
              <table className="branch-table">
                <thead>
                  <tr>
                    <th>Sr</th>
                    <th>Branch Details</th>
                    <th>State / District</th>
                    <th>Contact</th>
                    <th>Email / Mobile</th>
                    <th>Registration Date</th>
                    <th>Status</th>
                    <th>Approved / Rejected By</th>
                    <th>Approval Date</th>
                    <th>Remark</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredBranches.map((b, index) => {
                    const status = getApprovalStatus(b);
                    const isFinal =
                      status === "APPROVED" || status === "REJECTED";

                    return (
                      <tr key={b.id}>
                        <td>{index + 1}</td>

                        <td>
                          <div className="org-name">
                            {b.organizationName || "-"}
                          </div>
                          <div className="muted">User ID: {b.uid || b.id}</div>
                          <div className="muted">Role: State Branch</div>
                        </td>

                        <td>
                          <b>{b.state || "-"}</b>
                          <div className="muted">
                            District: {b.district || "-"}
                          </div>
                        </td>

                        <td>
                          <b>{b.name || "-"}</b>
                          <div className="muted">
                            Organization: {b.organizationName || "-"}
                          </div>
                        </td>

                        <td>
                          <b>{b.email || "-"}</b>
                          <div className="muted">{b.mobile || "-"}</div>
                        </td>

                        <td>{formatDateTime(b.createdAt)}</td>

                        <td>
                          <span
                            className={`status ${
                              status === "APPROVED"
                                ? "approved"
                                : status === "REJECTED"
                                ? "rejected"
                                : "pending"
                            }`}
                          >
                            {getStatusLabel(status)}
                          </span>
                          <div className="muted">
                            Firestore: {b.status || "-"}
                          </div>
                        </td>

                        <td>
                          <b>{b.approvedByName || "-"}</b>
                          <div className="muted">
                            {b.approvedByEmail || "-"}
                          </div>
                          <div className="muted">
                            {b.approvedByRole || "-"}
                          </div>
                        </td>

                        <td>
                          {formatDateTime(
                            b.headApprovalAt || b.roleApprovedAt
                          )}
                        </td>

                        <td>
                          {b.headApprovalRemark ||
                            b.roleApprovalRemark ||
                            b.shineAgriVerificationRemark ||
                            "-"}
                        </td>

                        <td>
                          <div className="action-cell">
                            <button
                              className="action-btn view"
                              onClick={() => setSelectedBranch(b)}
                            >
                              View
                            </button>

                            <button
                              className={`action-btn approve ${
                                isFinal ? "disabled-btn" : ""
                              }`}
                              disabled={isFinal}
                              onClick={() => updateStatus(b, "APPROVED")}
                            >
                              Approve
                            </button>

                            <button
                              className={`action-btn reject ${
                                isFinal ? "disabled-btn" : ""
                              }`}
                              disabled={isFinal}
                              onClick={() => updateStatus(b, "REJECTED")}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!filteredBranches.length && (
                    <tr>
                      <td colSpan="11" className="empty">
                        No branch registration found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {selectedBranch && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-head">
              <h3>State Branch Details</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedBranch(null)}
              >
                Close
              </button>
            </div>

            <div className="modal-body">
              <div className="info-box">
                <label>Organization Name</label>
                <b>{selectedBranch.organizationName || "-"}</b>
              </div>

              <div className="info-box">
                <label>Contact Person</label>
                <b>{selectedBranch.name || "-"}</b>
              </div>

              <div className="info-box">
                <label>Email</label>
                <b>{selectedBranch.email || "-"}</b>
              </div>

              <div className="info-box">
                <label>Mobile</label>
                <b>{selectedBranch.mobile || "-"}</b>
              </div>

              <div className="info-box">
                <label>State</label>
                <b>{selectedBranch.state || "-"}</b>
              </div>

              <div className="info-box">
                <label>District</label>
                <b>{selectedBranch.district || "-"}</b>
              </div>

              <div className="info-box">
                <label>Registration Date</label>
                <b>{formatDateTime(selectedBranch.createdAt)}</b>
              </div>

              <div className="info-box">
                <label>Status</label>
                <b>{getStatusLabel(getApprovalStatus(selectedBranch))}</b>
              </div>

              <div className="info-box">
                <label>Approved / Rejected By</label>
                <b>
                  {selectedBranch.approvedByName || "-"}
                  <br />
                  {selectedBranch.approvedByEmail || ""}
                </b>
              </div>

              <div className="info-box">
                <label>Approval Date</label>
                <b>
                  {formatDateTime(
                    selectedBranch.headApprovalAt ||
                      selectedBranch.roleApprovedAt
                  )}
                </b>
              </div>

              <div className="info-box">
                <label>User ID</label>
                <b>{selectedBranch.uid || selectedBranch.id}</b>
              </div>

              <div className="info-box">
                <label>Remark</label>
                <b>
                  {selectedBranch.headApprovalRemark ||
                    selectedBranch.roleApprovalRemark ||
                    "-"}
                </b>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="action-btn approve"
                disabled={
                  getApprovalStatus(selectedBranch) === "APPROVED" ||
                  getApprovalStatus(selectedBranch) === "REJECTED"
                }
                onClick={() => {
                  updateStatus(selectedBranch, "APPROVED");
                  setSelectedBranch(null);
                }}
              >
                Approve
              </button>

              <button
                className="action-btn reject"
                disabled={
                  getApprovalStatus(selectedBranch) === "APPROVED" ||
                  getApprovalStatus(selectedBranch) === "REJECTED"
                }
                onClick={() => {
                  updateStatus(selectedBranch, "REJECTED");
                  setSelectedBranch(null);
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}